package org.teddapp.project.finance

import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withContext
import org.teddapp.project.db.DateMath
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

/**
 * Moteur de récurrence Finance — exécuté au lancement de l'app et à l'ouverture de
 * l'écran Finance. Il n'y a pas de tâche de fond persistante : on rattrape « au
 * réveil » toutes les échéances dues.
 *
 * Deux responsabilités :
 *  - **Triggers** : pour chaque déclencheur actif dont `next_run_date <= today`,
 *    exécuter ses lignes (transfert d'un montant fixe source → destination) puis
 *    caler la prochaine échéance dans le futur. Si plusieurs échéances ont été
 *    manquées, on n'exécute **qu'une fois** (décision produit) et on saute à la
 *    prochaine date future.
 *  - **Listes de dépenses récurrentes** : pour chaque liste dont la réinitialisation
 *    est due, repasser les items à non-cochés (actual effacé) et avancer l'échéance.
 *
 * Les mouvements passent par les repos (`adjustBalance`) donc les lignes deviennent
 * « dirty » et se synchronisent normalement. Une transaction `transfer` est aussi
 * journalisée pour la traçabilité (insertion directe, sans réappliquer les effets de
 * solde déjà faits ici).
 */
object FinanceScheduler {

    private val mutex = Mutex()

    /** Exécute toutes les échéances dues à [today]. Idempotent dans la journée. */
    suspend fun runDue(repos: TeddRepositories, today: String = TODAY_ISO) = mutex.withLock {
        withContext(Dispatchers.Default) {
            runDueResets(repos, today)
        }
    }

    private suspend fun runDueResets(repos: TeddRepositories, today: String) {
        repos.db.expenseListQueries.selectDueReset(today).executeAsList().forEach { list ->
            // Mode recreate : on fige d'abord un instantané daté de la période écoulée.
            if (list.reset_mode == "recreate") {
                repos.db.expenseListQueries.selectById(list.id).executeAsOneOrNull()?.let { full ->
                    repos.expenseLists.archiveSnapshot(full, periodLabel = list.next_reset_date)
                }
            }
            repos.expenseLists.resetItems(list.id)
            val next = DateMath.nextAfter(list.next_reset_date, today, list.recurrence)
            repos.expenseLists.advanceReset(list.id, lastResetDate = today, nextResetDate = next)
        }
    }
}